/* eslint-disable import/no-unused-modules */
import React, { useEffect, useState } from 'react';
import { allBlogGuides } from 'contentlayer/generated';
import withApollo from '../../lib/withApollo';
import Header from 'components/Header/Header';
import ArticleNav from 'components/Post/Article/ArticleNav';
import BlogPosts from 'components/Blog/BlogPosts';
import BlogHeader from 'components/Blog/BlogHeader';
import Head from 'components/Header/Head';
import ssrGraphQLClient from '../../helpers/ssrGraphQLClient';
import { GET_POSTS } from '../../helpers/graphql/posts';
import { getUser } from '../../helpers/token';

const BlogPage = ({ posts }) => {
  const [isMember, setIsMember] = useState(false);

  useEffect(() => {
    if (getUser()) setIsMember(true);
  }, []);

  // Warm paper behind everything — same as the post page (SinglePost.js).
  useEffect(() => {
    document.body.classList.add('tf-paper');
    return () => document.body.classList.remove('tf-paper');
  }, []);

  return (
    <>
      <Head
        title="Blog"
        description="Guides, product news and stories to help travel bloggers start, manage and grow their blogs."
        seo={{ canonicalUrl: 'https://travelfeed.com/blog' }}
      />
      {isMember ? <Header /> : <ArticleNav />}
      <BlogHeader />
      <BlogPosts posts={posts} />
    </>
  );
};

// DB posts that are evergreen guides rather than announcements. New guides
// ship as git-managed MDX (content/blog/) instead of DB posts.
const DB_GUIDE_PERMLINKS = [
  'how-to-make-a-travel-blog-for-friends-and-family',
  'wordpress-vs-travelfeed-the-best-choice-for-travel-bloggers-2023',
  'how-to-start-a-travel-blog-on-instagram',
  'how-to-make-money-from-your-travel-blog-affiliate-marketing-for-travel-bloggers',
  'worldmappin-and-travelfeed-options-for-mapping-your-travel-posts',
];

// DB posts kept out of the index because they teach the retired editor. The
// step-by-step guide also redirects (next.config.js) — it is a how-to that the
// current editor guide supersedes. The 2019 announcement keeps its URL, since
// it is a dated changelog entry rather than instructions anyone would follow.
const RETIRED_DB_PERMLINKS = [
  'how-to-use-the-easyeditor-step-by-step-guide',
  'updates-first-witness-block-instagram-embeds-and-awesome-easyeditor',
];

export async function getStaticProps() {
  let dbPosts = [];
  try {
    const data = await ssrGraphQLClient(GET_POSTS, {
      isBlog: true,
      limit: 500,
      orderby: 'createdAt',
    });
    dbPosts = (data && data.posts) || [];
  } catch {
    // Backend hiccup: render the MDX guides alone; ISR retries the rest.
  }

  const guides = allBlogGuides.filter(guide => !guide.draft);

  const guideCards = guides.map(guide => ({
    key: `guide-${guide.slug}`,
    href: `/blog/${guide.slug}`,
    title: guide.title,
    excerpt: guide.description,
    thumb: guide.thumb ? { url: guide.thumb, blur: null } : null,
    createdAt: guide.publishedAt,
    readTime: guide.readTime,
    category: 'guide',
    location: null,
  }));

  // An MDX guide that shadows a DB permlink replaces its card entirely
  const shadowed = new Set(guides.map(guide => guide.slug));

  const dbCards = dbPosts
    .filter(
      post =>
        !shadowed.has(post.permlink) &&
        !RETIRED_DB_PERMLINKS.includes(post.permlink),
    )
    .map(post => ({
      key: `${post.author.username}-${post.permlink}`,
      // Card links go straight to the canonical /blog/ URL (the /@author/
      // route only 308s there)
      href: `/blog/${post.permlink}`,
      title: post.title,
      excerpt: post.excerpt || null,
      thumb: post.thumb || null,
      createdAt: post.createdAt,
      readTime: post.readTime || null,
      category: DB_GUIDE_PERMLINKS.includes(post.permlink) ? 'guide' : 'news',
      location: post.location || null,
    }));

  const posts = [...guideCards, ...dbCards].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
  );

  return { props: { posts }, revalidate: 3600 };
}

export default withApollo(BlogPage);
